import React from 'react';
import { SectionCard, TextField, TextAreaField, ImageField, Repeater } from '../../../components/admin/EditorFields';

const ICONS = [
  'CreditCard', 'Smartphone', 'Wine', 'Droplets', 'FlaskConical', 'Recycle',
  'Coffee', 'Sparkles', 'Zap', 'Package', 'Truck', 'Shield',
];

function IconSelect({ value, onChange, label = 'Icône' }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1.5 block">{label}</label>
      <select
        value={value || 'Sparkles'}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 rounded-xl border border-sky-200 px-3 text-sm"
      >
        {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
      </select>
    </div>
  );
}

/**
 * Helper : String-list Repeater (each item is a raw string)
 */
function StringListRepeater({ label, addLabel, items, path, setData, rows = 2, singleLine = false }) {
  return (
    <Repeater
      label={label}
      addLabel={addLabel}
      items={items || []}
      onChange={(next) => setData((d) => {
        const cp = JSON.parse(JSON.stringify(d));
        const keys = path.split('.');
        let cur = cp;
        for (let i = 0; i < keys.length - 1; i++) {
          cur[keys[i]] = cur[keys[i]] || {};
          cur = cur[keys[i]];
        }
        cur[keys[keys.length - 1]] = next;
        return cp;
      })}
      newItem={() => ''}
      renderItem={(item, _, i) => {
        const onChg = (v) => setData((d) => {
          const cp = JSON.parse(JSON.stringify(d));
          const keys = path.split('.');
          let cur = cp;
          for (let k = 0; k < keys.length - 1; k++) cur = cur[keys[k]] = cur[keys[k]] || {};
          const arr = cur[keys[keys.length - 1]] || [];
          arr[i] = v;
          cur[keys[keys.length - 1]] = arr;
          return cp;
        });
        const value = typeof item === 'string' ? item : '';
        return singleLine
          ? <TextField label={`Ligne ${i + 1}`} value={value} onChange={onChg} />
          : <TextAreaField label={`Ligne ${i + 1}`} rows={rows} value={value} onChange={onChg} />;
      }}
    />
  );
}

export default function HowItWorksEditor({ data, setData }) {
  const upd = (path, value) => setData((d) => {
    const next = JSON.parse(JSON.stringify(d || {}));
    const keys = path.split('.');
    let cur = next;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = cur[keys[i]] || {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
    return next;
  });

  return (
    <div className="space-y-5">
      {/* HERO */}
      <SectionCard title="Hero (en-tête)">
        <TextField label="Eyebrow" value={data.hero?.eyebrow} onChange={(v) => upd('hero.eyebrow', v)} />
        <div className="grid sm:grid-cols-3 gap-3">
          <TextField label="Titre - début" value={data.hero?.title} onChange={(v) => upd('hero.title', v)} />
          <TextField label="Titre en couleur" value={data.hero?.titleHighlight} onChange={(v) => upd('hero.titleHighlight', v)} />
          <TextField label="Titre - fin" value={data.hero?.titleSuffix} onChange={(v) => upd('hero.titleSuffix', v)} />
        </div>
        <TextAreaField label="Description" rows={3} value={data.hero?.description} onChange={(v) => upd('hero.description', v)} />
      </SectionCard>

      {/* INSTALLATION */}
      <SectionCard title="Installation" description="Le premier kiosque installé.">
        <TextField label="Titre" value={data.installation?.title} onChange={(v) => upd('installation.title', v)} />
        <ImageField label="Photo" value={data.installation?.photo} onChange={(v) => upd('installation.photo', v)} />
        <TextField label="Légende photo" value={data.installation?.photoCaption} onChange={(v) => upd('installation.photoCaption', v)} />
        <StringListRepeater label="Paragraphes" items={data.installation?.paragraphs} path="installation.paragraphs" setData={setData} rows={3} />
      </SectionCard>

      {/* ACCESSIBILITY */}
      <SectionCard title="Accessibilité 24/7">
        <TextField label="Titre" value={data.accessibility?.title} onChange={(v) => upd('accessibility.title', v)} />
        <Repeater
          label="Photos (2 photos secondaires)"
          items={data.accessibility?.photos || []}
          onChange={(next) => upd('accessibility.photos', next)}
          newItem={() => ''}
          renderItem={(item, _, i) => (
            <ImageField
              label={`Photo ${i + 1}`}
              value={typeof item === 'string' ? item : ''}
              onChange={(v) => setData((d) => {
                const cp = JSON.parse(JSON.stringify(d));
                cp.accessibility = cp.accessibility || {};
                cp.accessibility.photos = cp.accessibility.photos || [];
                cp.accessibility.photos[i] = v;
                return cp;
              })}
            />
          )}
        />
        <TextField label="Légende photos" value={data.accessibility?.photoCaption} onChange={(v) => upd('accessibility.photoCaption', v)} />
        <TextAreaField label="Texte horaires" rows={2} value={data.accessibility?.hoursText} onChange={(v) => upd('accessibility.hoursText', v)} />
        <TextField label="Intro stationnement" value={data.accessibility?.parkingIntro} onChange={(v) => upd('accessibility.parkingIntro', v)} />
        <StringListRepeater label="Items stationnement" items={data.accessibility?.parkingItems} path="accessibility.parkingItems" setData={setData} singleLine />
        <TextAreaField label="Accessibilité PMR / bus" rows={2} value={data.accessibility?.pmrText} onChange={(v) => upd('accessibility.pmrText', v)} />
      </SectionCard>

      {/* WATER QUALITY */}
      <SectionCard title="Qualité de l'eau">
        <TextField label="Titre" value={data.waterQuality?.title} onChange={(v) => upd('waterQuality.title', v)} />
        <TextAreaField label="Description" rows={3} value={data.waterQuality?.description} onChange={(v) => upd('waterQuality.description', v)} />
        <StringListRepeater label="Caractéristiques (liste à check)" items={data.waterQuality?.features} path="waterQuality.features" setData={setData} singleLine />
        <TextAreaField label="Purification (charbon actif + UV)" rows={3} value={data.waterQuality?.purification} onChange={(v) => upd('waterQuality.purification', v)} />
        <TextAreaField label="Note pétillante" rows={2} value={data.waterQuality?.sparklingNote} onChange={(v) => upd('waterQuality.sparklingNote', v)} />
        <TextAreaField label="Note future (microplastiques / PFAS)" rows={2} value={data.waterQuality?.futureNote} onChange={(v) => upd('waterQuality.futureNote', v)} />
      </SectionCard>

      {/* COMPARISON */}
      <SectionCard title="Comparaison (petit encart)">
        <TextAreaField label="Texte" rows={3} value={data.comparison?.text} onChange={(v) => upd('comparison.text', v)} />
      </SectionCard>

      {/* VIDEO */}
      <SectionCard title="Vidéo">
        <TextField label="Titre" value={data.video?.title} onChange={(v) => upd('video.title', v)} />
        <TextField label="URL de la vidéo (YouTube ou mp4)" value={data.video?.url} onChange={(v) => upd('video.url', v)} />
        <TextField label="Placeholder (si pas d'URL)" value={data.video?.placeholder} onChange={(v) => upd('video.placeholder', v)} />
      </SectionCard>

      {/* PRICING */}
      <SectionCard title="Tarifs">
        <TextField label="Titre" value={data.pricing?.title} onChange={(v) => upd('pricing.title', v)} />
        <TextAreaField label="Introduction" rows={2} value={data.pricing?.intro} onChange={(v) => upd('pricing.intro', v)} />
        <Repeater
          label="Prix au kiosque"
          items={data.pricing?.kiosque}
          onChange={(items) => upd('pricing.kiosque', items)}
          newItem={() => ({ label: '', price: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
              <TextField label="Prix" value={item.price} onChange={(v) => update({ price: v })} />
            </div>
          )}
        />
        <TextField label="Titre comparaisons" value={data.pricing?.comparisonTitle} onChange={(v) => upd('pricing.comparisonTitle', v)} />
        <Repeater
          label="Comparaisons prix concurrents"
          items={data.pricing?.comparisons}
          onChange={(items) => upd('pricing.comparisons', items)}
          newItem={() => ({ label: '', price: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
              <TextField label="Prix" value={item.price} onChange={(v) => update({ price: v })} />
            </div>
          )}
        />
      </SectionCard>

      {/* PAYMENT */}
      <SectionCard title="Modes de paiement">
        <TextField label="Titre" value={data.payment?.title} onChange={(v) => upd('payment.title', v)} />
        <TextField label="Introduction" value={data.payment?.intro} onChange={(v) => upd('payment.intro', v)} />
        <Repeater
          label="Options de paiement"
          items={data.payment?.options}
          onChange={(items) => upd('payment.options', items)}
          newItem={() => ({ icon: 'CreditCard', title: '', text: '' })}
          renderItem={(item, update) => (
            <div className="space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <IconSelect value={item.icon} onChange={(v) => update({ icon: v })} />
                <TextField label="Titre" value={item.title} onChange={(v) => update({ title: v })} />
              </div>
              <TextAreaField label="Texte" rows={3} value={item.text} onChange={(v) => update({ text: v })} />
            </div>
          )}
        />
      </SectionCard>

      {/* VOLUMES */}
      <SectionCard title="Volumes disponibles">
        <TextField label="Titre" value={data.volumes?.title} onChange={(v) => upd('volumes.title', v)} />
        <TextField label="Introduction" value={data.volumes?.intro} onChange={(v) => upd('volumes.intro', v)} />

        <Repeater
          label="Eau pétillante (volumes / prix)"
          items={data.volumes?.sparkling}
          onChange={(items) => upd('volumes.sparkling', items)}
          newItem={() => ({ volume: '', price: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Volume" value={item.volume} onChange={(v) => update({ volume: v })} />
              <TextField label="Prix" value={item.price} onChange={(v) => update({ price: v })} />
            </div>
          )}
        />

        <Repeater
          label="Eau nature (volumes / prix)"
          items={data.volumes?.nature}
          onChange={(items) => upd('volumes.nature', items)}
          newItem={() => ({ volume: '', price: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Volume" value={item.volume} onChange={(v) => update({ volume: v })} />
              <TextField label="Prix" value={item.price} onChange={(v) => update({ price: v })} />
            </div>
          )}
        />

        <TextAreaField label="Note (bandeau colorée)" rows={2} value={data.volumes?.note} onChange={(v) => upd('volumes.note', v)} />
        <ImageField label="Photo" value={data.volumes?.photo} onChange={(v) => upd('volumes.photo', v)} />
        <TextField label="Légende photo" value={data.volumes?.photoCaption} onChange={(v) => upd('volumes.photoCaption', v)} />
        <StringListRepeater label="Astuces / conseils" items={data.volumes?.tips} path="volumes.tips" setData={setData} rows={2} />
      </SectionCard>

      {/* CONTAINERS */}
      <SectionCard title="Contenants compatibles">
        <TextField label="Titre" value={data.containers?.title} onChange={(v) => upd('containers.title', v)} />
        <TextField label="Introduction" value={data.containers?.intro} onChange={(v) => upd('containers.intro', v)} />
        <Repeater
          label="Types de contenants"
          items={data.containers?.items}
          onChange={(items) => upd('containers.items', items)}
          newItem={() => ({ icon: 'Wine', label: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <IconSelect value={item.icon} onChange={(v) => update({ icon: v })} />
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
            </div>
          )}
        />
        <TextAreaField label="Recommandation" rows={2} value={data.containers?.recommendation} onChange={(v) => upd('containers.recommendation', v)} />
        <TextAreaField label="Note (bandeau)" rows={2} value={data.containers?.note} onChange={(v) => upd('containers.note', v)} />
      </SectionCard>

      {/* KIT */}
      <SectionCard title="Kit Kiosques à Eau (bouteilles + sac)">
        <TextField label="Titre" value={data.kit?.title} onChange={(v) => upd('kit.title', v)} />
        <TextField label="Introduction" value={data.kit?.intro} onChange={(v) => upd('kit.intro', v)} />
        <StringListRepeater label="Caractéristiques du kit" items={data.kit?.features} path="kit.features" setData={setData} rows={2} />
        <TextField label="Titre formules" value={data.kit?.formulasTitle} onChange={(v) => upd('kit.formulasTitle', v)} />
        <TextField
          label="Lien externe des 3 cartes (URL complète commençant par https://)"
          value={data.kit?.formulasLink}
          onChange={(v) => upd('kit.formulasLink', v)}
          hint="Chaque carte de formule redirige vers cette URL (nouvelle fenêtre). Laissez vide pour désactiver le lien."
        />
        <Repeater
          label="Formules proposées"
          items={data.kit?.formulas}
          onChange={(items) => upd('kit.formulas', items)}
          newItem={() => ({ label: '', price: '' })}
          renderItem={(item, update) => (
            <div className="grid sm:grid-cols-2 gap-3">
              <TextField label="Libellé" value={item.label} onChange={(v) => update({ label: v })} />
              <TextField label="Prix" value={item.price} onChange={(v) => update({ price: v })} />
            </div>
          )}
        />
        <TextAreaField label="Conclusion" rows={2} value={data.kit?.conclusion} onChange={(v) => upd('kit.conclusion', v)} />
        <ImageField label="Image du kit" value={data.kit?.image} onChange={(v) => upd('kit.image', v)} />
      </SectionCard>

      {/* FINAL CTA */}
      <SectionCard title="CTA final">
        <TextField label="Titre" value={data.finalCta?.title} onChange={(v) => upd('finalCta.title', v)} />
        <TextAreaField label="Description" rows={3} value={data.finalCta?.description} onChange={(v) => upd('finalCta.description', v)} />
      </SectionCard>
    </div>
  );
}
